/**
 * <collection-quiz>
 * Reads JSON config from a child <script type="application/json" data-ref="quiz-config">,
 * walks the user through questions, then redirects to the collection URL with all
 * selected `filter.*` query params concatenated.
 *
 * Expected JSON shape:
 *   {
 *     "title": "...",
 *     "intro": "optional sub-heading",
 *     "questions": [
 *       {
 *         "q": "Question text",
 *         "options": [
 *           { "label": "Option 1", "emoji": "🏙️", "filter": "filter.p.m.veste.usage=urbain" },
 *           { "label": "Skip", "filter": "" }
 *         ]
 *       }
 *     ]
 *   }
 *
 * `filter` is either a single `key=value` token or multiple joined with `&`
 * (e.g. "filter.v.price.gte=80&filter.v.price.lte=150"). Empty string = no filter.
 */
class CollectionQuiz extends HTMLElement {
  constructor() {
    super();
    this.state = {
      step: -1, // -1 = intro screen, [0..n-1] = questions, n = results
      answers: [],
      config: null,
    };
  }

  connectedCallback() {
    const configEl = this.querySelector('script[data-ref="quiz-config"]');
    if (!configEl) {
      this._renderError('Configuration du quiz introuvable.');
      return;
    }

    try {
      this.state.config = JSON.parse(configEl.textContent.trim());
    } catch (err) {
      console.error('[collection-quiz] Invalid JSON:', err);
      this._renderError("Configuration du quiz invalide (JSON).");
      return;
    }

    if (!this.state.config.questions || !this.state.config.questions.length) {
      this._renderError('Aucune question configurée.');
      return;
    }

    this.shellEl = this.querySelector('[data-ref="quiz-shell"]');

    const cfg = this.state.config;
    const hasIntro = (cfg.title && cfg.title.trim()) || (cfg.intro && cfg.intro.trim());
    if (!hasIntro) this.state.step = 0;

    this._render();

    this._consumeScrollFlag();
  }

  // -------- Rendering ----------------------------------------------------

  _render() {
    const isResults = this.state.step >= this.state.config.questions.length;
    if (this.shellEl) this.shellEl.classList.toggle('collection-quiz__shell--results', isResults);
    if (this.state.step === -1) return this._renderIntro();
    if (isResults) return this._renderResults();
    return this._renderQuestion();
  }

  _renderIntro() {
    const { title, intro } = this.state.config;
    const cta = this.dataset.ctaStart || 'Commencer';
    this.shellEl.innerHTML = `
      <div class="collection-quiz__header">
        ${title ? `<h3 class="collection-quiz__title">${this._esc(title)}</h3>` : ''}
        ${intro ? `<p class="collection-quiz__intro">${this._esc(intro)}</p>` : ''}
      </div>
      <button type="button" class="collection-quiz__cta" data-ref="quiz-start">${this._esc(cta)}</button>
    `;
    this.shellEl.querySelector('[data-ref="quiz-start"]').addEventListener('click', () => {
      this.state.step = 0;
      this._render();
    });
  }

  _renderQuestion() {
    const { questions, title } = this.state.config;
    const idx = this.state.step;
    const q = questions[idx];
    const progressTpl = this.dataset.progressLabel || 'Question {current} sur {total}';
    const progressText = progressTpl
      .replace('{current}', idx + 1)
      .replace('{total}', questions.length);
    const progressPct = ((idx) / questions.length) * 100;
    const backLabel = this.dataset.ctaBack || '← Précédent';

    const optionsHtml = q.options.map((opt, i) => `
      <li class="collection-quiz__option">
        <button type="button" data-option-index="${i}">
          ${opt.emoji ? `<span class="collection-quiz__option-emoji">${this._esc(opt.emoji)}</span>` : ''}
          <span class="collection-quiz__option-label">${this._esc(opt.label)}</span>
        </button>
      </li>
    `).join('');

    this.shellEl.innerHTML = `
      ${title && idx === 0 ? `<h3 class="collection-quiz__title">${this._esc(title)}</h3>` : ''}
      <div class="collection-quiz__progress">
        <span>${this._esc(progressText)}</span>
        <span class="collection-quiz__progress-bar" aria-hidden="true">
          <span style="width:${progressPct}%"></span>
        </span>
      </div>
      <p class="collection-quiz__question">${this._esc(q.q)}</p>
      <div class="collection-quiz__options-wrap">
        <ul class="collection-quiz__options">${optionsHtml}</ul>
      </div>
      <div class="collection-quiz__footer">
        ${idx > 0 ? `<button type="button" class="collection-quiz__back" data-ref="quiz-back">${this._esc(backLabel)}</button>` : '<span></span>'}
      </div>
    `;

    this.shellEl.querySelectorAll('button[data-option-index]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        const i = Number(ev.currentTarget.dataset.optionIndex);
        this._selectOption(i);
      });
    });

    const backBtn = this.shellEl.querySelector('[data-ref="quiz-back"]');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.state.step = Math.max(0, this.state.step - 1);
        this.state.answers = this.state.answers.slice(0, this.state.step);
        this._render();
      });
    }
  }

  _renderResults() {
    const ctaLabel = (this.state.config && this.state.config.cta) || this.dataset.ctaSeeResults || 'Voir les résultats';

    this.shellEl.innerHTML = `
      <div class="collection-quiz__results-simple">
        <button type="button" class="collection-quiz__cta" data-ref="quiz-go">${this._esc(ctaLabel)}</button>
      </div>
    `;

    this.shellEl.querySelector('[data-ref="quiz-go"]').addEventListener('click', () => {
      this._goToResultsWithFallback();
    });
  }

  async _goToResultsWithFallback() {
    const btn = this.shellEl.querySelector('[data-ref="quiz-go"]');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="collection-quiz__spinner" aria-hidden="true"></span>';
    }
    // Flag a scroll-to-grid for after the filtered page reloads.
    try { sessionStorage.setItem('cq:scrollToGrid', '1'); } catch (e) {}
    const base = this.dataset.collectionUrl || window.location.pathname;
    // Filters are in question order = priority order (Q1 = strongest intent,
    // last question = weakest). Relax by dropping from the end until results
    // appear. Collection-agnostic: works for any filter namespace.
    let active = this.state.answers.map(a => (a.filter || '').trim()).filter(Boolean);
    while (true) {
      const url = active.length ? `${base}?${active.join('&')}` : base;
      const count = await this._countResults(url);
      if (count !== 0 || active.length === 0) {
        window.location.assign(url);
        return;
      }
      active.pop();
    }
  }

  _consumeScrollFlag() {
    let flag = null;
    try { flag = sessionStorage.getItem('cq:scrollToGrid'); } catch (e) {}
    if (!flag) return;
    try { sessionStorage.removeItem('cq:scrollToGrid'); } catch (e) {}

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const scroll = () => {
      let offset = 0;
      const header = document.querySelector('header, .header, [data-ref="header"], sticky-header');
      if (header) {
        const pos = getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') offset = header.getBoundingClientRect().height;
      }
      const top = grid.getBoundingClientRect().top + window.scrollY - offset - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    // Let the filtered grid + first images settle before scrolling.
    requestAnimationFrame(() => setTimeout(scroll, 80));
  }

  async _countResults(url) {
    try {
      const resp = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
      const html = await resp.text();
      if (/Aucun produit trouv/i.test(html)) return 0;
      const matches = html.match(/\/products\/[a-z0-9_-]+/gi);
      return matches ? new Set(matches).size : 0;
    } catch (e) {
      return -1; // unknown — treat as "has results" to not block redirect
    }
  }

  _renderError(msg) {
    const shell = this.querySelector('[data-ref="quiz-shell"]') || this;
    shell.innerHTML = `<p class="collection-quiz__placeholder">${this._esc(msg)}</p>`;
  }

  // -------- State / URL --------------------------------------------------

  _selectOption(i) {
    const q = this.state.config.questions[this.state.step];
    const opt = q.options[i];
    this.state.answers[this.state.step] = {
      label: opt.label,
      filter: opt.filter || '',
    };
    this.state.step += 1;
    this._render();
  }

  _buildResultsUrl() {
    const base = this.dataset.collectionUrl || (window.location.pathname);
    const tokens = this.state.answers
      .map((a) => (a.filter || '').trim())
      .filter(Boolean);
    const query = tokens.join('&');
    return query ? `${base}?${query}` : base;
  }

  // -------- Utils --------------------------------------------------------

  _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }
}

if (!customElements.get('collection-quiz')) {
  customElements.define('collection-quiz', CollectionQuiz);
}
